import { useState, useEffect } from "react";
import Modal from "react-modal";
import { HelpCircle } from "lucide-react";
import api from '../../service/api';
import "../../css/Anamnese.css";
import "../../css/Katz.css";

const PERGUNTAS_KATZ = [
    { id: 0, titulo: "BANHO", texto: "1. BANHO", descricao: "A avaliação da atividade 'BANHAR-SE' é considerada em relação ao uso do chuveiro, da banheira e ao ato de esfregar-se.", tipo: "radio" },
    { id: 1, titulo: "VESTIR", texto: "2. VESTIR", descricao: "Ato de pegar roupas no armário e se vestir (botões, fechos). Exclui calçar sapatos.", tipo: "radio" },
    { id: 2, titulo: "BANHEIRO", texto: "3. BANHEIRO", descricao: "Ir ao banheiro, higienizar-se e arrumar as roupas. Inclui uso de auxiliares como 'comadres'.", tipo: "radio" },
    { id: 3, titulo: "TRANSFERÊNCIA", texto: "4. TRANSFERÊNCIA", descricao: "Movimento de sair da cama e sentar-se em uma cadeira e vice-versa.", tipo: "radio" },
    { id: 4, titulo: "CONTINÊNCIA", texto: "5. CONTINÊNCIA", descricao: "Controle de eliminação de urina e fezes. O uso de fraldas ou cateter caracteriza dependência.", tipo: "radio" },
    { id: 5, titulo: "ALIMENTAÇÃO", texto: "6. ALIMENTAÇÃO", descricao: "Ato de dirigir a comida do prato à boca. Exclui o preparo ou corte do alimento.", tipo: "radio" },
];

const OPCOES_KATZ = [
    { id: 0, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 1, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 2, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 3, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 4, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 5, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
];

function Katz({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
    const [etapa, setEtapa] = useState(1);
    const [isNextModal, setIsNextModal] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const [respostasSelecionadas, setRespostasSelecionadas] = useState(
        PERGUNTAS_KATZ.map(p => ({ id: p.id, valor: "" }))
    );

    useEffect(() => {
        if (evaluationData?.katz?.respostas) {
            setRespostasSelecionadas(evaluationData.katz.respostas);
        }
    }, [evaluationData]);

    const totalEtapas = 2;
    const porcentagemConclusao = (etapa / totalEtapas) * 100;

    // --- LÓGICA DE AÇÃO ---

    const atualizarResposta = (perguntaId, novoValor) => {
        setRespostasSelecionadas(prev => prev.map(item =>
            item.id === perguntaId ? { ...item, valor: novoValor } : item
        ));
    };

    const avancarEtapa = () => setEtapa(prev => prev + 1);
    const retrocederEtapa = () => setEtapa(prev => (prev > 1 ? prev - 1 : prev));

    const handleKatzRegistrar = async () => {
        const payload = {
            katz: { respostas: respostasSelecionadas },
            evaluationId
        };

        try {
            const response = await api.post(`/api/student/${aluno.id}/evaluation/katz`, payload, { withCredentials: true });
            if (response.status === 200) {
                if (response.data.evaluationId && setEvaluationId) {
                    if (!evaluationId) {
                        if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
                        aluno.avaliacoesRealizadas.push({
                            id: response.data.evaluationId,
                            data: new Date().toISOString()
                        });
                    }
                    setEvaluationId(response.data.evaluationId);
                }
                if (updateEvaluationData) {
                    updateEvaluationData({ katz: payload.katz });
                }
                setIsNextModal(true);
            }
        } catch (error) {
            console.error('Erro ao salvar Katz:', error);
        }
    };

    // --- LÓGICA DE CÁLCULO ---

    // No Katz, apenas "Sem ajuda" soma ponto para independência
    const pontuacao = respostasSelecionadas.reduce((acc, r) => (r.valor === "Sem ajuda" ? acc + 1 : acc), 0);

    const obterClassificacao = () => {
        if (respostasSelecionadas.some(r => r.valor === "")) return "Responda todas as questões";
        if (pontuacao === 6) return "Independente";
        if (pontuacao >= 3) return "Dependência Moderada";
        return "Dependência Importante";
    };

    // --- RENDERIZAÇÃO ---

    const renderPerguntaKatz = (pergunta) => {
        const rObj = respostasSelecionadas.find(item => item.id === pergunta.id);
        const valorAtual = rObj ? rObj.valor : "";
        const opcoesDaPergunta = OPCOES_KATZ.find(opt => opt.id === pergunta.id)?.opcoes || [];

        return (
            <div className="question-item" key={pergunta.id}>
                <div className="standard-title">{pergunta.texto}</div>
                <p className="question-desc">{pergunta.descricao}</p>
                <div className="answer-container">
                    {opcoesDaPergunta.map(opcao => (
                        <label className="answer-item" key={opcao}>
                            <input
                                type="radio"
                                className="answer-radio"
                                name={`katz-radio-${pergunta.id}`}
                                checked={valorAtual === opcao}
                                onChange={() => atualizarResposta(pergunta.id, opcao)}
                            />
                            <p className="answer-title">{opcao}</p>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="avaliacao-container">
            <div className="avaliacao-content">

                {/* ETAPA 1: Perguntas */}
                {etapa === 1 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            {PERGUNTAS_KATZ.slice(0, 3).map(renderPerguntaKatz)}
                        </div>
                        <div className="questions-right">
                            {PERGUNTAS_KATZ.slice(3, 6).map(renderPerguntaKatz)}
                        </div>
                    </div>
                )}

                {/* ETAPA 2: Resultado */}
                {etapa === 2 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            <div className="resultado-final-box">
                                <div className="standard-title">Resultado do Índice de Katz</div>
                                <h3 className="pad-top-10">Classificação: {obterClassificacao()}</h3>
                                <p className="pad-top-10">Pontuação de Independência: <strong>{pontuacao} de 6</strong></p>
                            </div>
                        </div>
                        <div className="questions-right">
                            {/* Espaço para observações ou informações adicionais se desejar */}
                        </div>
                    </div>
                )}

                {/* FOOTER PADRONIZADO */}
                <div className="pagination-footer">
                    <div className="sub-pagination-footer">
                        <div className="sub-pagination-buttons">
                            <button className="next-prev-button" type="button" onClick={retrocederEtapa} disabled={etapa === 1}>
                                Voltar
                            </button>
                            {etapa < totalEtapas ? (
                                <button className="next-prev-button" type="button" onClick={avancarEtapa}>
                                    Avançar
                                </button>
                            ) : (
                                <button className="next-prev-button" type="button" onClick={handleKatzRegistrar}>
                                    Registrar
                                </button>
                            )}
                        </div>
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${porcentagemConclusao}%` }}></div>
                        </div>
                    </div>

                    <div className="info-box-container" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
                        <div className="info-button">
                            <HelpCircle size={20} />
                            Mais informações
                        </div>
                        {showInfo && (
                            <div className="info-box-content">
                                <p>
                                    O Questionário de Katz é um instrumento utilizado para avaliar a capacidade funcional do idoso na realização das Atividades Básicas da Vida Diária (ABVDs), como banho, vestir-se, higiene pessoal, transferência, continência e alimentação.
                                    <br/><br/>
                                    Seu principal objetivo é identificar o nível de independência ou necessidade de assistência nessas tarefas essenciais, permitindo uma análise prática da funcionalidade e autonomia do avaliado.
                                    <br/><br/>
                                    Essa ferramenta é amplamente utilizada para monitorar a saúde funcional, orientar intervenções e contribuir para estratégias que promovam maior independência, segurança e qualidade de vida na população idosa.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    isOpen={isNextModal}
                    onRequestClose={() => setIsNextModal(false)}
                    contentLabel="Confirmacao"
                    className="modal-next"
                    overlayClassName="popup-overlay"
                >
                    <p className="popupNext-h1">Katz finalizado!</p>
                    <p className="popupNext-h2">Deseja ir para o Lawton?</p>
                    <div className="sub-pagination-buttons">
                        <button className="next-prev-button" type="button" onClick={() => setActiveTab("Lawton")}>Sim</button>
                        <button className="next-prev-button" type="button" onClick={() => setIsNextModal(false)}>Não</button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}

export default Katz;